/**
 * APM Plus 监控配置
 * 火山引擎 APM 前端监控 SDK 配置
 */

import apmClient from '@apmplus/web';
import type { BrowserInitConfig } from '@apmplus/web';

// 支持上报的环境：**仅 prod**。
// 闸门关闭时 APM 不 init —— 官方通道（jsError/ajax/fetch/pageview）与自定义
// （事件/日志/性能指标）全部不会发出，也不产生任何 APM 网络请求。
// 注：test/pre 的 PID 配置保留在 .env.test/.env.pre，但不再生效，仅供日后需要
// 灰度验证时把模式加回本数组即可恢复。
const REPORT_ENABLED_MODES = ['prod'];

/**
 * 当前环境是否开启 APM 上报。
 * 与 initAPM 内部判断同源，供 main.tsx 决定是否启动「依赖 APM 才有意义」的监控器
 * （聚合计数器、崩溃窗口、GPU/内存监控）—— 闸门关闭时它们仍在空转采样，纯属浪费。
 */
export const isAPMEnabled = (): boolean => REPORT_ENABLED_MODES.includes(import.meta.env.MODE);

// SSE 长连接端点：单条流可持续数十秒，若被 ajax/fetch 插件当普通请求统计会把接口 P95 打歪，
// 接口看板与告警从此不可信。SSE 观测改由业务侧自研探针承担（语义更准）。
const SSE_IGNORE_URLS = [/\/chat\/stream/, /\/chat\/resume-stream/];
// APM/Clarity 自身域名：防止监控请求把自己也监控进去，形成自噬循环
const SELF_IGNORE_URLS = [/clarity\.ms/, /apmplus\.volces\.com/];
// 前后端链路串联的目标域名：命中后 SDK 自动注入 trace header，失败请求可直接给后端定位服务端日志
const TRACE_ORIGINS: (string | RegExp)[] = [/^\/v1\//];
if (import.meta.env.VITE_APP_BASE_URL) {
  TRACE_ORIGINS.push(import.meta.env.VITE_APP_BASE_URL as string);
}

// APM 配置接口
interface APMConfig {
  aid: number;
  token: string;
  domain?: string;
  pid: string;
  userId?: string;
  release?: string;
  env?: string;
  enabledInDev?: boolean;
}

// 默认配置
const defaultConfig: Partial<APMConfig> = {
  domain: 'apmplus.volces.com',
  release: '1.0.0',
  env: import.meta.env.VITE_APP_ENV || 'development',
  enabledInDev: false, // 开发环境默认不启用
};

// 从环境变量获取配置
const getAPMConfigFromEnv = (): Partial<APMConfig> => {
  return {
    aid: Number(import.meta.env.VITE_APM_AID) || 0,
    token: import.meta.env.VITE_APM_TOKEN || '',
    domain: import.meta.env.VITE_APM_DOMAIN,
    pid: import.meta.env.VITE_APM_PID || window.location.pathname,
    userId: import.meta.env.VITE_APM_USER_ID,
    release: import.meta.env.VITE_APM_RELEASE,
    env: import.meta.env.VITE_APP_ENV,
    enabledInDev: import.meta.env.VITE_APM_ENABLE_DEV === 'true',
  };
};

// 合并配置
const mergeConfig = (envConfig: Partial<APMConfig>): APMConfig => {
  const config = { ...defaultConfig, ...envConfig };

  // 必填字段检查
  if (!config.aid || !config.token) {
    throw new Error('APM 配置错误: aid 和 token 是必填字段');
  }

  return config as APMConfig;
};

// 初始化 APM
export const initAPM = (): void => {
  try {
    // 确保 DOM 已经准备好，特别是 document.body 已经存在
    if (!document || !document.body) {
      console.warn('⚠️ DOM 未准备好，延迟初始化 APM');
      // 如果 DOM 未准备好，等待 DOMContentLoaded 事件后再初始化
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          initAPM();
        }, { once: true });
      }
      return;
    }

    // 注：会话标识不再在此预生成——唯一真值源改为 SDK 内置 sessionId（见 getSessionId）。
    // 原「每次加载生成一个」既与官方通道对不上，也会在刷新时切断多页旅程。

    // 仅 prod 上报；test/pre/dev 一律不 init，APM 侧零请求、零数据
    if (!isAPMEnabled()) {
      console.log('🔧 APM 在当前环境中已禁用（仅 prod 上报）');
      return;
    }

    const envConfig = getAPMConfigFromEnv();
    const config = mergeConfig(envConfig);

    // 生成用户ID（如果未提供）
    const userId = config.userId || generateUserId();

    // APM 初始化配置
    const apmConfig: BrowserInitConfig = {
      aid: config.aid,
      token: config.token,
      domain: config.domain,
      pid: config.pid,
      userId: userId,
      release: config.release,
      env: config.env,
      plugins: {
        jsError: {
          ignoreErrors: [
            'The play', 'play() failed', 'The operation', 'The request',
            'Script error.', 'ResizeObserver loop',
          ],
          dedupe: true, // 内置去重，省掉自研指纹逻辑
          captureGlobalAsync: true,
        },
        ajax: {
          ignoreUrls: SELF_IGNORE_URLS,
          // ⚠️ trace 已临时关闭（2026-09-04 紧急止血）：
          //   开启 trace 后 SDK 会自动注入 W3C Trace Context 标准头 baggage（用于跨服务传业务上下文），
          //   但生产后端 CORS 的 Access-Control-Allow-Headers 未允许 baggage，导致所有 /v1/* 跨域请求
          //   preflight 失败 → 全站接口不可用。
          //   恢复条件（必须同时满足）：
          //     1) 后端在 Access-Control-Allow-Headers 中加入 baggage（以及 traceparent / tracestate）；
          //     2) 与后端对齐 trace ID 响应头字段名（此前方案列为 P0-16 待办，已主动延期）；
          //     3) 重新上线前需在 pre 环境用 OPTIONS 预检确认 CORS 不再拦截。
          trace: false,
        },
        // SSE 端点必须排除，否则数十秒的长连接会把接口 P95 打歪，接口看板从此不可信
        fetch: {
          ignoreUrls: [...SELF_IGNORE_URLS, ...SSE_IGNORE_URLS],
          trace: false, // 原因与恢复条件同 ajax
        },
        resourceError: { dedupe: true, gatherPath: true }, // 资源【错误】侧，必须保留全量
        // 面包屑 = 归因与复现的地基（不产生独立上报，仅随错误携带）
        breadcrumb: {
          dom: true, // 自动记录点击 xpath / 按键，「知道点了什么才炸」
          maxBreadcrumbs: 50, // 画布/生成类操作密集，30 条仅够十几秒
          onMaxBreadcrumbs: (list) => {
            // 满了优先淘汰 DOM 噪声，保留业务语义与 http 线索
            const keep = list.filter(b => b.type !== 'dom');
            const doms = list.filter(b => b.type === 'dom').slice(-15);
            return [...keep, ...doms].sort((a, b) => a.timestamp - b.timestamp).slice(-50);
          },
          onAddBreadcrumb: (b) =>
            // 隐私：键盘输入内容一律脱敏
            b.category === 'ui.keypress' ? { ...b, message: '[input]' } : b,
        },
        pageview: { routeMode: 'history', apdex: 1 }, // 一切「率」的分母，全量保留
        // 噪声治理：资源加载成功无观测价值，关掉不影响错误捕获（resourceError 独立负责）
        resource: false,
        // 长任务与自建 gpuMemoryMonitor 长任务监控重复（自建已调优至 200ms + 去重）
        performance: { longtask: false },
        // action / heatmap 不开：行为分析交由 Clarity 承担，避免每次点击一条的巨量噪声
        blankScreen: { screenshot: true, mask: true, quality: 0.1 }, // 白屏带截图，mask 防泄露
      },
      // transport 使用 SDK 默认值（内置 XHR + useBeacon），不手动覆盖
      // 崩溃兜底通过 gpuMemoryMonitor 的 pagehide 事件 + getSender().flush() 实现
    };

    // 初始化 APM 客户端
    apmClient.init(apmConfig);

    // 设置用户上下文
    apmClient.config({
      userId: userId,
      pid: config.pid,
    });

    // 启动监控
    apmClient.start();

    console.log('✅ APM 监控已启动', {
      aid: config.aid,
      pid: config.pid,
      env: config.env,
      userId: userId,
    });
  } catch (error) {
    console.error('❌ APM 初始化失败:', error);
  }
};

// 生成用户ID
const generateUserId = (): string => {
  // localstorage 中存储的是anmo_ai_user_info，其中用户id是里面的id
  const userInfo = JSON.parse(localStorage.getItem('anmo_ai_user_info') || '{}');
  const existingUserId = userInfo.id ? userInfo.id : userInfo.user_id;
  if (existingUserId) {
    return existingUserId;
  }

  // 生成新的用户ID
  const userId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  localStorage.setItem('amai_user_id', userId);
  return userId;
};

// 手动上报自定义事件
export const reportCustomEvent = (
  name: string,
  metrics?: Record<string, number>,
  categories?: Record<string, string>
) => {
  if (!isAPMEnabled()) return; // 仅 prod 上报
  try {
    apmClient.sendEvent?.({
      name,
      metrics,
      categories: { session_id: getSessionId(), ...categories },
    });
  } catch (error) {
    console.error('APM 自定义事件上报失败:', error);
  }
};

// 手动上报自定义性能指标（走性能看板，自动获得 P50/P90/P95 分位与趋势）
// ⚠️ 前提：指标名须先在控制台「性能指标管理」注册，否则上报成功但平台不消费
export const reportCustomPerfMetric = (
  name: string,
  value: number,
  extra?: Record<string, string>
) => {
  if (!isAPMEnabled()) return; // 仅 prod 上报
  try {
    apmClient.sendCustomPerfMetric?.({ name, value, isCustom: true, extra });
  } catch (error) {
    console.error('APM 自定义性能指标上报失败:', error);
  }
};

// 手动上报自定义日志
export const reportCustomLog = (
  content: string,
  level: 'debug' | 'info' | 'warn' | 'error' = 'info',
  extra?: Record<string, string | number>
) => {
  if (!isAPMEnabled()) return; // 仅 prod 上报
  try {
    apmClient.sendLog?.({
      content,
      level,
      extra: { session_id: getSessionId(), ...extra },
    });
  } catch (error) {
    console.error('APM 自定义日志上报失败:', error);
  }
};

// 手动上报错误
// react 第三参专为 React 归因设计：传入后异常看板可直接看到 componentStack，
// 一眼定位到具体组件（如 CanvasNode → ReactFlow → CanvasPage）。
export const reportError = (
  error: Error,
  extra?: Record<string, string>,
  react?: { version: string; componentStack: string }
) => {
  if (!isAPMEnabled()) return; // 仅 prod 上报
  try {
    apmClient.captureException?.(error, extra, react);
  } catch (err) {
    console.error('APM 错误上报失败:', err);
  }
};

// 设置用户信息
export const setUser = (userId: string, extra?: Record<string, string>) => {
  try {
    apmClient.config({
      userId,
    });

    if (extra) {
      apmClient['context.merge']?.(extra);
    }

    // 保存到本地存储
    localStorage.setItem('amai_user_id', userId);
  } catch (error) {
    console.error('APM 设置用户信息失败:', error);
  }
};

// ─── 会话标识（session_id）─────────────────────────────────────────────────
// 唯一真值源 = 火山 SDK 内置的 sessionId（官方字段，SDK 于 init() 时生成）。
// 之所以必须对齐它，而不是继续用自造值：
//   1) 官方通道（jsError/ajax/fetch/pageview）自动携带的就是它。只有对齐，自定义
//      事件才能与官方异常在同一会话维度下串联，否则同一会话在 APM 里是两拨数据。
//   2) Clarity 的 apm_session tag 写的也是它（见 main.tsx）。对齐后，自定义事件
//      （含全部画布事件）才具备按 session 跳回放的前提。
//   3) SDK 的 sessionId 带 storageExpires 生命周期（跨页面刷新保持）；而此前自造的
//      `s_<ts>_<rand>` 每次加载都重新生成，多页旅程会被刷新切断。
let fallbackSessionId: string | null = null;

const generateSessionId = (): string =>
  `s_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

/**
 * 读取 SDK 内置 sessionId。
 * apmClient.config() 无参调用即官方 getter，但其返回类型为 Config | undefined，
 * 且顶层类型未显式声明 sessionId，故需做安全取值。
 */
const readSdkSessionId = (): string | null => {
  try {
    const cfg = apmClient.config() as { sessionId?: string } | undefined;
    return cfg?.sessionId ?? null;
  } catch {
    // config() 在极端情况可能抛错；返回 null 由调用方降级，绝不影响主流程
    return null;
  }
};

/**
 * 获取会话标识：优先 SDK 内置，取不到时懒生成兜底。
 * 不缓存 SDK 值 —— 会话过期时 SDK 会轮换 sessionId，实时读才能与官方通道保持一致。
 * 自定义事件仅在错误/阻断态触发（量极小），每次读一次 config() 的开销可忽略。
 */
export const getSessionId = (): string => {
  const sdkId = readSdkSessionId();
  if (sdkId) return sdkId;
  // 兜底：上报被禁用（dev）或 SDK 尚未 init 时懒生成，避免空值形成 'unknown' 垃圾维度
  return (fallbackSessionId ??= generateSessionId());
};

// 设置页面信息
export const setPage = (pid: string) => {
  try {
    apmClient.config({
      pid,
    });
  } catch (error) {
    console.error('APM 设置页面信息失败:', error);
  }
};

// 导出 APM 客户端实例（用于高级用法）
export { apmClient };
export default {
  initAPM, reportCustomEvent, reportCustomPerfMetric, reportCustomLog, reportError, setUser, setPage,
};
